import React, { useState } from "react"
import { Card, Tag, Typography, Button, Flex, Empty, Skeleton } from "antd"
import { EllipsisOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import PropTypes from "prop-types"
import IconWithBg from "ui/IconWithBg"
import CustomDrawer from "ui/Drawer"
import { STATUS_COLOR, formatValue, formatDateRange, formatCap } from "../discountUtils"
import styles from "./index.module.css"

const { Text } = Typography

const DiscountMobileList = ({ discounts, getMenuItems, isMutating, isLoading }) => {
  const { t } = useTranslation()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState(null)

  const openDrawer = (record) => {
    setSelectedRecord(record)
    setDrawerOpen(true)
  }

  const closeDrawer = () => {
    setDrawerOpen(false)
    setSelectedRecord(null)
  }

  const menuItems = selectedRecord ? getMenuItems(selectedRecord) : []

  if (isLoading) {
    return (
      <Flex vertical gap={12} className="p-m">
        {[1, 2, 3].map((i) => (
          <Card key={i} className={styles.card}>
            <Skeleton active paragraph={{ rows: 3 }} />
          </Card>
        ))}
      </Flex>
    )
  }

  if (!discounts.length) {
    return <Empty description={t("settings.no_discounts", "No discounts found")} />
  }

  return (
    <>
      <Flex vertical gap={12}>
        {discounts.map((record) => (
          <Card key={record.id} className={styles.card}>
              <Flex justify="space-between" align="flex-start">
                <Flex vertical gap={6} style={{ flex: 1, minWidth: 0 }}>
                  <Flex align="center" gap={8} wrap>
                    <Text strong className={styles.name}>{record.name}</Text>
                    <Tag>{record.code}</Tag>
                    <Tag color={STATUS_COLOR[record.status] ?? "default"}>
                      {record.status === "active" ? t("settings.status_active") : t("settings.status_inactive")}
                    </Tag>
                  </Flex>

                  <Flex gap={16} wrap>
                    <Flex gap={4}>
                      <Text type="secondary">{t("settings.discount_col_value")}:</Text>
                      <Text>{formatValue(record)}</Text>
                    </Flex>
                  </Flex>

                  <Flex gap={4}>
                    <Text type="secondary">{t("settings.discount_col_validity")}:</Text>
                    <Text>{formatDateRange(record.valid_from, record.valid_until)}</Text>
                  </Flex>

                  <Flex gap={16} wrap>
                    <Flex gap={4}>
                      <Text type="secondary">{t("settings.discount_col_usage")}:</Text>
                      <Text>{record.usage_count ?? 0} / {formatCap(record.usage_cap)}</Text>
                    </Flex>
                    <Flex gap={4}>
                      <Text type="secondary">{t("settings.discount_col_per_guest_limit", "Per-Guest")}:</Text>
                      <Text>{formatCap(record.per_user_usage_cap)}</Text>
                    </Flex>
                  </Flex>
                </Flex>

                <IconWithBg
                  onClick={() => openDrawer(record)}
                  icon={<EllipsisOutlined />}
                  className={styles.moreBtn}
                />
              </Flex>
          </Card>
        ))}
      </Flex>

      <CustomDrawer open={drawerOpen} title="Actions" setOpen={closeDrawer}>
        <Flex vertical gap={10} className="w-full">
          {menuItems.map((item) => (
            <Button
              key={item.key}
              color={item.danger ? "danger" : "primary"}
              variant="outlined"
              size="large"
              disabled={isMutating}
              onClick={() => {
                closeDrawer()
                item.onClick?.()
              }}
              className="w-full"
            >
              {item.label}
            </Button>
          ))}
        </Flex>
      </CustomDrawer>
    </>
  )
}

DiscountMobileList.propTypes = {
  discounts: PropTypes.array.isRequired,
  getMenuItems: PropTypes.func.isRequired,
  isMutating: PropTypes.bool,
  isLoading: PropTypes.bool
}

export default DiscountMobileList
